import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useAppContext } from '../AppContext';
import * as LucideIcons from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AdminPage = () => {
    const { currentUserProfile, appFunctions, setMessage } = useAppContext();
    const [isLoading, setIsLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [momentSummary, setMomentSummary] = useState(null);
    const [completenessSummary, setCompletenessSummary] = useState(null);
    const [languageSummary, setLanguageSummary] = useState(null);

    const handleInitialize = async () => {
        setIsLoading(true);
        setSummary(null);
        setMessage("Starting data initialization... This may take several minutes.", "info");

        try {
            const initializeData = httpsCallable(appFunctions, 'initializeData');
            const result = await initializeData();
            setSummary(result.data.summary);
            setMessage("Data initialization complete!", "success");
        } catch (error) {
            console.error("Error initializing data:", error);
            setMessage(`Error: ${error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInitializeMoments = async () => {
        setIsLoading(true);
        setMomentSummary(null);
        setMessage("Starting moment initialization...", "info");

        try {
            const initializeMomentsFn = httpsCallable(appFunctions, 'initializeAllMoments');
            const result = await initializeMomentsFn();
            setMomentSummary(result.data);
            setMessage(result.data.message, "success");
        } catch (error) {
            console.error("Error initializing moments:", error);
            setMessage(`Error: ${error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackfillMetadata = async () => {
        setIsLoading(true);
        setLanguageSummary(null);
        setMessage("Starting metadata backfill for moments and whispers...", "info");
        try {
            const backfillFn = httpsCallable(appFunctions, 'backfillContentMetadata');
            const result = await backfillFn();
            setLanguageSummary(result.data.message);
            setMessage("Metadata backfill complete!", "success");
        } catch (error) {
            console.error("Error backfilling metadata:", error);
            setMessage(`Error: ${error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEnsureCompleteness = async () => {
        setIsLoading(true);
        setCompletenessSummary(null);
        setMessage("Ensuring profile completeness... This may take a moment.", "info");

        try {
            const ensureProfileCompleteness = httpsCallable(appFunctions, 'ensureProfileCompleteness');
            const result = await ensureProfileCompleteness();
            setCompletenessSummary(result.data.message);
            setMessage("Profile completeness check complete!", "success");
        } catch (error) {
            console.error("Error ensuring profile completeness:", error);
            setMessage(`Error: ${error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    // Render a loading state while the user profile is being fetched.
    if (!currentUserProfile) {
        return (
            <div className="text-center p-8">
                <LucideIcons.Loader size={48} className="mx-auto mb-4 animate-spin" />
                <p>Loading user data...</p>
            </div>
        );
    }

    // Check for 'admin' or 'owner' role for access.
    const canAccess = currentUserProfile.role === 'admin' || currentUserProfile.role === 'owner';

    if (!canAccess) {
        return (
            <div className="text-center p-8 text-red-500">
                <LucideIcons.ShieldAlert size={48} className="mx-auto mb-4" />
                <h1 className="text-2xl font-bold">Access Denied</h1>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 max-w-2xl mx-auto pb-24">
            <Helmet>
                <title>Admin Dashboard - Whispers of Harmony</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <h1 className="text-3xl font-bold text-center mb-6 text-indigo-400 font-sans">Admin Control Panel</h1>
            
            {/* --- General Data Initialization --- */}
            <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                <h2 className="text-xl font-semibold mb-4">General Data Initialization</h2>
                <p className="text-gray-400 mb-4">
                    This tool will scan all users, whispers, and moments. It will create missing user profiles and add any missing required fields to existing documents to ensure they conform to the current data structure. This is useful after a data migration or to fix inconsistencies.
                </p>
                <div className="flex space-x-4">
                    <button
                        onClick={handleInitialize}
                        disabled={isLoading}
                        className="w-full px-8 py-3 bg-indigo-600 text-white font-bold rounded-full hover:bg-indigo-700 transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {isLoading ? <div className="action-spinner" /> : <LucideIcons.DatabaseZap size={18} />}
                        {isLoading ? 'Initializing...' : 'Initialize All Data'}
                    </button>
                    <button
                        onClick={handleEnsureCompleteness}
                        disabled={isLoading}
                        className="w-full px-8 py-3 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {isLoading ? <div className="action-spinner" /> : <LucideIcons.UserCheck size={18} />}
                        {isLoading ? 'Checking...' : 'Ensure Profile Completeness'}
                    </button>
                </div>
                <div className="mt-4">
                    <button
                        onClick={handleBackfillMetadata}
                        disabled={isLoading}
                        className="w-full px-8 py-3 bg-teal-600 text-white font-bold rounded-full hover:bg-teal-700 transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {isLoading ? <div className="action-spinner" /> : <LucideIcons.Languages size={18} />}
                        {isLoading ? 'Backfilling...' : 'Backfill Content Metadata'}
                    </button>
                </div>

                {summary && (
                    <div className="mt-6 p-4 bg-gray-900 rounded-lg">
                        <h3 className="text-lg font-semibold mb-2">Initialization Summary:</h3>
                        <pre className="text-sm text-gray-300 whitespace-pre-wrap">
                            {JSON.stringify(summary, null, 2)}
                        </pre>
                    </div>
                )}
                {languageSummary && (
                    <div className="mt-6 p-4 bg-gray-900 rounded-lg">
                        <h3 className="text-lg font-semibold mb-2">Language Backfill Summary:</h3>
                        <p className="text-sm text-gray-300">{languageSummary}</p>
                    </div>
                )}
                {completenessSummary && (
                    <div className="mt-6 p-4 bg-gray-900 rounded-lg">
                        <h3 className="text-lg font-semibold mb-2">Completeness Summary:</h3>
                        <p className="text-sm text-gray-300">{completenessSummary}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPage;
